import DOMPurify from 'dompurify'
import { AGREEMENT_SIGNING_BRACKET_PURIFY_OPTIONS } from './agreementPurifyOptions'

/**
 * Optional "[field name]" placeholders in agreement HTML (rich text).
 * Used for creator highlight, preview highlight, and recipient inline inputs.
 */

export const BRACKET_FIELD_TEXT_RE = /\[[^\]]+\]/g

export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function escapeAttr(s) {
  return escapeHtml(s).replace(/'/g, '&#39;')
}

/** Remove highlight spans so brackets are plain text again. */
export function unwrapBracketHighlights(rootEl) {
  if (!rootEl) return
  const spans = rootEl.querySelectorAll('span.agreement-bracket-highlight')
  spans.forEach((span) => {
    const t = span.ownerDocument.createTextNode(span.textContent)
    span.parentNode.replaceChild(t, span)
  })
}

/** Remove editor/preview highlight spans before signing-time parse (recipient + merge). */
export function stripBracketHighlightsFromHtml(htmlString) {
  if (typeof htmlString !== 'string' || !htmlString) return htmlString || ''
  if (!htmlString.includes('agreement-bracket-highlight')) return htmlString
  try {
    const doc = new DOMParser().parseFromString(`<div id="__bf_strip__">${htmlString}</div>`, 'text/html')
    const root = doc.getElementById('__bf_strip__')
    if (!root) return htmlString
    unwrapBracketHighlights(root)
    return root.innerHTML
  } catch {
    return htmlString
  }
}

/**
 * Wrap complete [bracket] sequences in text nodes with highlight span.
 * Mutates DOM under rootEl (typically the contenteditable editor).
 */
export function wrapBracketHighlightsInElement(rootEl) {
  if (!rootEl) return
  const doc = rootEl.ownerDocument
  const walker = doc.createTreeWalker(rootEl, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      let p = node.parentElement
      while (p && p !== rootEl) {
        const tag = p.tagName
        if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT
        p = p.parentElement
      }
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const textNodes = []
  let cur
  while ((cur = walker.nextNode())) textNodes.push(cur)

  for (const textNode of textNodes) {
    const text = textNode.nodeValue
    if (!text || !text.includes('[')) continue
    const re = BRACKET_FIELD_TEXT_RE
    re.lastIndex = 0
    if (!re.test(text)) continue
    re.lastIndex = 0
    const frag = doc.createDocumentFragment()
    let last = 0
    let m
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) frag.appendChild(doc.createTextNode(text.slice(last, m.index)))
      const span = doc.createElement('span')
      span.className = 'agreement-bracket-highlight'
      span.textContent = m[0]
      frag.appendChild(span)
      last = m.index + m[0].length
    }
    if (last < text.length) frag.appendChild(doc.createTextNode(text.slice(last)))
    textNode.parentNode.replaceChild(frag, textNode)
  }
}

export function normalizeBracketHighlightsInEditor(rootEl) {
  if (!rootEl) return
  unwrapBracketHighlights(rootEl)
  wrapBracketHighlightsInElement(rootEl)
}

/** For preview / read-only HTML string (already sanitized). */
export function wrapBracketHighlightsInHtmlString(html) {
  if (!html || !html.includes('[')) return html
  try {
    const doc = new DOMParser().parseFromString(`<div id="__bf_root__">${html}</div>`, 'text/html')
    const root = doc.getElementById('__bf_root__')
    if (!root) return html
    wrapBracketHighlightsInElement(root)
    return root.innerHTML
  } catch {
    return html
  }
}

const VOID_TAGS = new Set(['br', 'hr', 'img', 'input', 'col', 'meta', 'link', 'area', 'base', 'embed', 'source', 'track', 'wbr'])

function openingTag(node) {
  const tag = node.tagName.toLowerCase()
  let s = `<${tag}`
  for (const attr of node.attributes) {
    s += ` ${attr.name}="${escapeAttr(attr.value)}"`
  }
  s += VOID_TAGS.has(tag) ? ' />' : '>'
  return s
}

/**
 * Split sanitized HTML into alternating static HTML chunks and editable slots.
 * @returns {Array<{type:'html', html:string}|{type:'slot', index:number, label:string}>}
 */
export function buildBracketPartsFromHtml(htmlString, enabled) {
  if (!enabled || typeof htmlString !== 'string') {
    return [{ type: 'html', html: htmlString || '' }]
  }
  const html = stripBracketHighlightsFromHtml(htmlString)
  if (!html.includes('[')) {
    return [{ type: 'html', html }]
  }
  try {
    const doc = new DOMParser().parseFromString(`<div id="__bf_parts__">${html}</div>`, 'text/html')
    const root = doc.getElementById('__bf_parts__')
    if (!root) return [{ type: 'html', html }]

    const parts = []
    let buf = []
    let slotIdx = 0

    const flushBuf = () => {
      if (buf.length) {
        parts.push({ type: 'html', html: buf.join('') })
        buf = []
      }
    }

    const walk = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const t = node.nodeValue || ''
        const re = BRACKET_FIELD_TEXT_RE
        let last = 0
        let m
        while ((m = re.exec(t)) !== null) {
          if (m.index > last) buf.push(escapeHtml(t.slice(last, m.index)))
          flushBuf()
          const inner = m[0].slice(1, -1).trim()
          parts.push({ type: 'slot', index: slotIdx++, label: inner || 'Field' })
          last = m.index + m[0].length
        }
        if (last < t.length) buf.push(escapeHtml(t.slice(last)))
        return
      }
      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase()
        if (tag === 'script' || tag === 'style') return
        buf.push(openingTag(node))
        if (!VOID_TAGS.has(tag)) {
          for (const c of node.childNodes) walk(c)
          buf.push(`</${tag}>`)
        }
      }
    }

    for (const c of root.childNodes) walk(c)
    flushBuf()
    return parts.length ? parts : [{ type: 'html', html }]
  } catch {
    return [{ type: 'html', html: html || '' }]
  }
}

/** Slot indices in `parts` whose `values[index]` is empty after trim. */
export function getEmptyBracketSlotIndexes(parts, values) {
  const empty = []
  for (const p of parts) {
    if (p.type !== 'slot') continue
    const v = values[p.index]
    if (v == null || String(v).trim() === '') empty.push(p.index)
  }
  return empty
}

/** Merge slot values into final HTML (no brackets). Values keyed by slot index. */
export function mergeBracketPartsToHtml(parts, values) {
  let out = ''
  for (const p of parts) {
    if (p.type === 'html') out += p.html
    else {
      const raw = values[p.index]
      const v = raw == null ? '' : String(raw).trim()
      out += escapeHtml(v)
    }
  }
  return out
}

/**
 * One HTML string with inline inputs where slots were, so inputs stay inside headings/paragraphs.
 * Values are applied in the DOM by the recipient component (avoid replacing innerHTML on each keystroke).
 */
export function mergeBracketPartsToSigningHtml(parts) {
  let raw = ''
  for (const p of parts) {
    if (p.type === 'html') raw += p.html
    else {
      const label = escapeAttr(p.label || 'Field')
      const i = Number(p.index)
      const safeIdx = Number.isFinite(i) && i >= 0 ? i : 0
      raw += `<textarea rows="1" class="agreement-bracket-input" data-bf-slot="${safeIdx}" placeholder="${label}" aria-label="${label}" aria-invalid="false" autocomplete="off"></textarea>`
    }
  }
  return DOMPurify.sanitize(raw, AGREEMENT_SIGNING_BRACKET_PURIFY_OPTIONS)
}

/** Caret offset as flat index in textContent of root (for contenteditable restore). */
export function getCaretTextOffset(root, sel) {
  if (!root || !sel || sel.rangeCount === 0) return null
  const range = sel.getRangeAt(0)
  if (!root.contains(range.startContainer)) return null
  const pre = range.cloneRange()
  pre.selectNodeContents(root)
  pre.setEnd(range.startContainer, range.startOffset)
  return pre.toString().length
}

export function setCaretTextOffset(root, offset) {
  if (!root || typeof offset !== 'number' || offset < 0) return
  const doc = root.ownerDocument
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null)
  let remaining = offset
  let node
  while ((node = walker.nextNode())) {
    const len = node.nodeValue.length
    if (remaining <= len) {
      const r = doc.createRange()
      r.setStart(node, remaining)
      r.collapse(true)
      const sel = doc.getSelection()
      sel.removeAllRanges()
      sel.addRange(r)
      return
    }
    remaining -= len
  }
  try {
    const r = doc.createRange()
    r.selectNodeContents(root)
    r.collapse(false)
    const sel = doc.getSelection()
    sel.removeAllRanges()
    sel.addRange(r)
  } catch {
    /* ignore */
  }
}
